import { FormEvent, useState } from "react";
import { KeyRound, Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import type { ManualSection } from "@/content/manualTypes";

type Props = {
  onUnlock: (sections: ManualSection[]) => void;
};

const ManualGate = ({ onUnlock }: Props) => {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("manual-access", {
        body: { code: value.trim() },
      });
      if (fnError || !data?.sections) {
        setError(
          "Incorrect password. Please check your installation kit or contact your Cetasol partner.",
        );
        return;
      }
      onUnlock(data.sections as ManualSection[]);
    } catch {
      setError("Something went wrong. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center px-4 py-16">
      <div className="w-full max-w-md rounded-3xl border border-border bg-card/60 p-8 backdrop-blur sm:p-10">
        <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl border border-primary/40 bg-primary/10">
          <Lock className="h-5 w-5 text-primary" aria-hidden />
        </div>

        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Installation portal</h1>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          This guide is reserved for Cetasol customers and installation partners. Enter the access
          password from your installation kit to continue.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-4" noValidate>
          <div className="space-y-2">
            <label htmlFor="manual-password" className="text-sm font-medium text-foreground">
              Password
            </label>
            <Input
              id="manual-password"
              type="password"
              autoComplete="off"
              autoFocus
              value={value}
              onChange={(event) => {
                setValue(event.target.value);
                setError(null);
              }}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? "manual-password-error" : undefined}
              placeholder="Enter access password"
            />
            {error && (
              <p id="manual-password-error" role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <KeyRound className="mr-2 h-4 w-4" aria-hidden />
            )}
            Open manual
          </Button>
        </form>
      </div>
    </main>
  );
};

export default ManualGate;
